import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import { Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import AddIcon from '@mui/icons-material/Add'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { getTermList } from 'app/redux/store/features/terms'
import TermGridFilter from './components/TermGridFilter'
import TermGridData from './components/TermGridData'
import { Capitalize } from 'app/utils/components/StringUtils/stringUtils'
import { useSnackbar } from 'notistack'

const sortColumnToServerProperty = (columnName) => {
    if (!columnName) return 'TermYear'
    const map = {
        termYear: 'TermYear',
        termName: 'TermName',
        termNumber: 'TermNumber',
        isCurrent: 'IsCurrent',
        dateStart: 'DateStart',
        dateEnd: 'DateEnd',
        createTime: 'CreateTime',
        updateTime: 'UpdateTime',
        createdBy: 'CreatedBy',
    }
    return map[columnName] ?? Capitalize(columnName)
}

const TermsGrid = () => {
    const { state } = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const [filter, setFilter] = useState({
        termYear: null,
        termNumber: null,
        termName: '',
        sort: 'TermYear',
        isDescending: false,
    })
    const { terms, loading, error } = useSelector((state) => state.terms)
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [currentPage, setCurrentPage] = useState(0)
    const pageSize = 25

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) handleError(error)
    }, [loading])

    useEffect(() => {
        const shouldFetch = !state?.navigateFrom || !terms.data?.length
        if (shouldFetch) {
            setApiCallInProgress(true)
            dispatch(getTermList({ ...filter, skip: 0, take: pageSize }))
        }
    }, [state?.navigateFrom, dispatch])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    function init(initialValue) {
        return {
            gridFilterVisible: initialValue.gridFilterVisible,
            filterIconColor: initialValue.filterIconColor,
        }
    }

    function userReducer(state, action) {
        switch (action.type) {
            case 'set-grid-filter':
                return {
                    ...state,
                    gridFilterVisible: action.payload,
                }
            case 'set-filter-icon-color':
                return {
                    ...state,
                    filterIconColor: action.payload,
                }
            default:
                return state
        }
    }

    const [termGrid, setTermGrid] = React.useReducer(
        userReducer,
        {
            gridFilterVisible: false,
            filterIconColor: false,
        },
        init
    )

    const applyFilter = useMutation({
        mutationFn: (currentfilter) => {
            dispatch(getTermList({ ...currentfilter, skip: 0, take: pageSize }, true))
            setFilter(currentfilter)
        },
    })

    const onPageChange = (pageNumber) => {
        const currentFilter = { ...filter, skip: pageSize * pageNumber, take: pageSize }
        dispatch(getTermList(currentFilter))
        setFilter(currentFilter)
    }

    const onColumnSort = (sortOrder) => {
        const sortProperty = sortColumnToServerProperty(sortOrder?.[0]?.columnName)
        const isDescending =
            sortProperty === 'TermYear'
                ? sortOrder[0].direction !== 'desc'
                : sortOrder[0].direction !== 'asc'
        const currentFilter = {
            ...filter,
            sort: sortProperty,
            isDescending,
            skip: 0,
            take: pageSize,
        }
        dispatch(getTermList(currentFilter))
        setFilter(currentFilter)
        setCurrentPage(0)
    }

    const onRefresh = () => {
        setApiCallInProgress(true)
        const refreshFilter = { ...filter, skip: 0, take: pageSize }
        dispatch(getTermList(refreshFilter, true))
        setFilter(refreshFilter)
        setCurrentPage(0)
    }

    const setGridFilter = React.useCallback(
        (value) => {
            setTermGrid({ type: 'set-grid-filter', payload: value })
        },
        [setTermGrid]
    )

    const onAddTerm = () => {
        navigate('/courses/setup/term/add')
    }

    const setFilterIconColor = React.useCallback(
        (value) => {
            setTermGrid({ type: 'set-filter-icon-color', payload: value })
        },
        [setTermGrid]
    )

    return (
        <JumboCardQuick
            noWrapper
            title={
                <Typography color={'common.white'} variant={'h3'}>
                    <AutorenewRoundedIcon
                        fontSize={'large'}
                        onClick={onRefresh}
                        sx={{
                            mb: -1.5,
                            mr: 0.5,
                            ml: -2,
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                    Term List
                </Typography>
            }
            headerSx={{
                height: 40,
                bgcolor: 'success.main',
            }}
            action={
                <Stack direction={'row'} spacing={4} sx={{ mt: -2 }}>
                    <AddIcon
                        fontSize={'large'}
                        variant={'contained'}
                        sx={{
                            pt: 0,
                            pb: 0,
                            color: 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={onAddTerm}
                    />
                    <FilterAltIcon
                        fontSize='large'
                        sx={{
                            color:
                                termGrid.filterIconColor && !termGrid.gridFilterVisible
                                    ? 'success.main'
                                    : 'common.white',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                        onClick={() => setGridFilter(!termGrid.gridFilterVisible)}
                    />
                </Stack>
            }>
            <Grid>
                <TermGridFilter
                    mutation={applyFilter}
                    display={termGrid.gridFilterVisible ? '' : 'none'}
                    filterIconColor={(data) => setFilterIconColor(data)}
                />
            </Grid>
            <Grid>
                <TermGridData
                    onPageChange={onPageChange}
                    onColumnSort={onColumnSort}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                    handleError={handleError}
                />
            </Grid>
        </JumboCardQuick>
    )
}
export default TermsGrid
